/**
 * AdMob native integration (Capacitor only).
 * This module is a no-op on the web — banners only show inside the
 * Android/iOS native shell built via Capacitor.
 *
 * Setup:
 *   1. Replace TEST_APP_IDS below with your real AdMob App ID after
 *      creating apps at https://apps.admob.com/.
 *   2. Replace TEST_BANNER_ID with your production Ad Unit ID before release.
 *   3. Run `yarn build && npx cap sync` to copy the plugin into the native projects.
 *   4. Inside Android Studio: add the App ID to AndroidManifest.xml meta-data.
 *      Inside Xcode: add GADApplicationIdentifier to Info.plist.
 */
import { Capacitor } from '@capacitor/core';

// Production AdMob IDs (Matbakhi — created on 2026-02-21)
export const ADMOB_APP_ID_ANDROID = 'ca-app-pub-6074010825138021~3373706163';
export const ADMOB_APP_ID_IOS     = 'ca-app-pub-3940256099942544~1458002511';   // TODO: replace once iOS app is registered in AdMob
export const ADMOB_BANNER_ID_ANDROID = 'ca-app-pub-6074010825138021/5014090892';
export const ADMOB_BANNER_ID_IOS     = 'ca-app-pub-3940256099942544/2934735716'; // TODO: replace once iOS app is registered in AdMob
// Interstitial (full-screen) ad unit — shown every Nth recipe view to non-premium users.
export const ADMOB_INTERSTITIAL_ID_ANDROID = 'ca-app-pub-6074010825138021/3591599529'; // Production unit (created 2026-02-23)
export const ADMOB_INTERSTITIAL_ID_IOS     = 'ca-app-pub-3940256099942544/4411468910'; // TODO: replace once iOS app is registered in AdMob
// Rewarded ad unit — user voluntarily watches, gets 24h Premium unlock.
// Highest-eCPM ad format ($10-50 CPM vs $1-3 for interstitials).
// Production unit for Matbakhi (created 2026-02).
export const ADMOB_REWARDED_ID_ANDROID = 'ca-app-pub-6074010825138021/9157484455';
export const ADMOB_REWARDED_ID_IOS     = 'ca-app-pub-3940256099942544/1712485313'; // Google test ID for iOS — replace once iOS app is registered

let initialized = false;

export async function initAdMob() {
  if (initialized) return;
  if (!Capacitor.isNativePlatform()) return;            // skip on web
  try {
    const { AdMob } = await import('@capacitor-community/admob');
    await AdMob.initialize({
      requestTrackingAuthorization: true,
      initializeForTesting: false,                      // production mode — real ads
    });
    initialized = true;
  } catch (e) {
    console.warn('[AdMob] init failed', e);
  }
}

export async function showBanner() {
  // ❌ Banner ads are DISABLED across the app.
  //
  // We initially added it to the login screen so it would never overlap
  // real content, but AdMob's native banner view is unreliable to tear down
  // across route changes and can persist visually into pages where we
  // explicitly don't want it. Interstitials (already fired after login and
  // on app open) generate ~10× the eCPM anyway, so we lose almost nothing.
  return;
}

export async function hideBanner() {
  // No-op. Kept exported so callers don't need conditionals.
  return;
}

// ────────────────────────────────────────────────────────────────────────
// Interstitial (full-screen) ads
// ────────────────────────────────────────────────────────────────────────
// Frequency: show after every Nth recipe view. Tuned for monetisation
// without annoying users — Google guidelines recommend NOT showing
// interstitials on app launch or back-to-back.
const INTERSTITIAL_FREQUENCY = 4;
const RECIPE_VIEW_KEY = 'matbakhi_recipe_views';
let interstitialReady = false;

/** Preload an interstitial in the background so it shows instantly when needed. */
export async function prepareInterstitial() {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const { AdMob } = await import('@capacitor-community/admob');
    const isIOS = Capacitor.getPlatform() === 'ios';
    await AdMob.prepareInterstitial({
      adId: isIOS ? ADMOB_INTERSTITIAL_ID_IOS : ADMOB_INTERSTITIAL_ID_ANDROID,
      isTesting: false,
    });
    interstitialReady = true;
  } catch (e) {
    console.warn('[AdMob] prepareInterstitial failed', e);
    interstitialReady = false;
  }
}

/**
 * Call this every time the user opens a recipe. Once the user has opened
 * INTERSTITIAL_FREQUENCY recipes, a full-screen ad is shown and the counter
 * resets. The next ad is preloaded in the background.
 *
 * Pass `isPremium: true` to skip ads entirely for paying subscribers.
 */
export async function maybeShowInterstitial({ isPremium = false } = {}) {
  if (!Capacitor.isNativePlatform()) return;
  if (isPremium) return;
  try {
    const prev = parseInt(localStorage.getItem(RECIPE_VIEW_KEY) || '0', 10) || 0;
    const next = prev + 1;
    if (next < INTERSTITIAL_FREQUENCY) {
      localStorage.setItem(RECIPE_VIEW_KEY, String(next));
      return;
    }
    // Threshold reached → show the ad and reset the counter.
    localStorage.setItem(RECIPE_VIEW_KEY, '0');
    if (!interstitialReady) await prepareInterstitial();
    if (!interstitialReady) return;
    const { AdMob } = await import('@capacitor-community/admob');
    await AdMob.showInterstitial();
    interstitialReady = false;
    // Preload the next one immediately so the next trigger is instant.
    prepareInterstitial().catch(() => {});
  } catch (e) {
    console.warn('[AdMob] maybeShowInterstitial failed', e);
  }
}


// ────────────────────────────────────────────────────────────────────────
// One-shot interstitial — bypasses the recipe-view counter. Used for:
//   • immediately after successful login (welcome moment)
//   • on every app open when an existing session is detected
//     (throttled to once per `APP_OPEN_THROTTLE_MS` so users opening the
//      app frequently don't see ads back-to-back).
// ────────────────────────────────────────────────────────────────────────
const APP_OPEN_LAST_KEY = 'matbakhi_app_open_ad_at';
const APP_OPEN_THROTTLE_MS = 30 * 60 * 1000; // 30 minutes between app-open ads

export async function showInterstitialNow({ isPremium = false, throttleKey = null } = {}) {
  if (!Capacitor.isNativePlatform()) return false;
  if (isPremium) return false;
  // Optional throttling — for app-open scenario only.
  if (throttleKey) {
    const last = parseInt(localStorage.getItem(throttleKey) || '0', 10) || 0;
    if (Date.now() - last < APP_OPEN_THROTTLE_MS) return false;
  }
  try {
    if (!interstitialReady) await prepareInterstitial();
    if (!interstitialReady) return false;
    const { AdMob } = await import('@capacitor-community/admob');
    await AdMob.showInterstitial();
    interstitialReady = false;
    if (throttleKey) localStorage.setItem(throttleKey, String(Date.now()));
    prepareInterstitial().catch(() => {});
    return true;
  } catch (e) {
    console.warn('[AdMob] showInterstitialNow failed', e);
    return false;
  }
}

export const APP_OPEN_AD_KEY = APP_OPEN_LAST_KEY;


// ────────────────────────────────────────────────────────────────────────
// Rewarded ads — the highest-eCPM ad format.
// User CHOOSES to watch a full ad in exchange for a reward (24h Premium
// unlock, unlocked recipe, etc.). No annoyance, higher revenue.
// ────────────────────────────────────────────────────────────────────────
let rewardedReady = false;

export async function prepareRewarded() {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const { AdMob } = await import('@capacitor-community/admob');
    const isIOS = Capacitor.getPlatform() === 'ios';
    await AdMob.prepareRewardVideoAd({
      adId: isIOS ? ADMOB_REWARDED_ID_IOS : ADMOB_REWARDED_ID_ANDROID,
      isTesting: false,
    });
    rewardedReady = true;
  } catch (e) {
    console.warn('[AdMob] prepareRewarded failed', e);
    rewardedReady = false;
  }
}

/**
 * Show a rewarded ad and resolve with `true` if the user watched to the end
 * (and thus earned the reward), `false` if they dismissed early or an error
 * occurred. Next ad is preloaded automatically.
 *
 * On web this returns `true` after a 300ms delay so devs can iterate the
 * reward UI without needing a native build.
 */
export async function showRewardedAd() {
  if (!Capacitor.isNativePlatform()) {
    await new Promise(r => setTimeout(r, 300));
    return true;
  }
  try {
    const { AdMob, RewardAdPluginEvents } = await import('@capacitor-community/admob');
    if (!rewardedReady) await prepareRewarded();
    if (!rewardedReady) return false;
    // Race the "reward earned" and "dismissed" events. AdMob fires exactly
    // one of them per ad presentation.
    const rewarded = new Promise((resolve) => {
      const cleanups = [];
      const finish = (ok) => {
        cleanups.forEach(fn => { try { fn(); } catch { /* ignore */ } });
        resolve(ok);
      };
      const p1 = AdMob.addListener(RewardAdPluginEvents.Rewarded, () => finish(true));
      const p2 = AdMob.addListener(RewardAdPluginEvents.Dismissed, () => finish(false));
      const p3 = AdMob.addListener(RewardAdPluginEvents.FailedToLoad, () => finish(false));
      Promise.all([p1, p2, p3]).then(subs => {
        cleanups.push(...subs.map(s => () => s.remove()));
      });
    });
    await AdMob.showRewardVideoAd();
    rewardedReady = false;
    const earned = await rewarded;
    // Preload next ad in background regardless of outcome.
    prepareRewarded().catch(() => {});
    return earned;
  } catch (e) {
    console.warn('[AdMob] showRewardedAd failed', e);
    return false;
  }
}
